/**
 * The pieces of the application frame that are not specific to either side:
 * the rail, its collapse state, the ⌘K search and the account menu.
 *
 * Only these are shared. The merchant dashboard (`AppShell`) and the Super Admin
 * console (`SuperAdminLayout`) each own their layout and topbar, so nothing
 * merchant-scoped — a store switcher, a wallet, a plan card — can reach the
 * console through a forgotten `variant` check.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth.js'
import { api } from '../lib/api.js'
import { initials } from '../lib/format.js'
import { Menu, MenuItem } from './ui.js'
import { navLeaves, type NavBadge, type NavLeaf, type NavNode, type NavSection } from './nav.js'
import { BoltIcon, ChevronDownIcon, GearIcon, LogOutIcon, SearchIcon, UserIcon } from './icons.js'

const COLLAPSE_KEY = 'outfalak.sidebar.collapsed'

/** The rail's collapsed and mobile-drawer state, as `.shell` modifier classes. */
export function useRail(): { classes: string[]; close: () => void; toggle: () => void } {
  const location = useLocation()
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSE_KEY) === '1')
  const [mobileOpen, setMobileOpen] = useState(false)

  useEffect(() => {
    localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0')
  }, [collapsed])

  // Any navigation closes the mobile drawer. Without this, tapping a link on a
  // phone leaves the rail covering the page you just asked for.
  useEffect(() => {
    setMobileOpen(false)
  }, [location.pathname])

  return {
    classes: [collapsed ? 'is-collapsed' : '', mobileOpen ? 'is-open' : ''].filter(Boolean),
    close: () => setMobileOpen(false),
    toggle: () => (window.innerWidth <= 960 ? setMobileOpen((v) => !v) : setCollapsed((v) => !v)),
  }
}

// ── Sidebar ─────────────────────────────────────────────────────────────────

export function Sidebar({ sections, homeTo, children }: { sections: NavSection[]; homeTo: string; children?: ReactNode }): ReactNode {
  const location = useLocation()

  // A group opens when the current route is inside it, and stays wherever the
  // merchant last put it after that. Recomputing on every render instead would
  // slam a group shut the moment they opened it and clicked nothing.
  const [open, setOpen] = useState<Record<string, boolean>>({})

  useEffect(() => {
    setOpen((current) => {
      const next = { ...current }
      let changed = false
      for (const section of sections) {
        for (const item of section.items) {
          if (item.children?.some((child) => location.pathname.startsWith(child.to)) && !next[item.label]) {
            next[item.label] = true
            changed = true
          }
        }
      }
      return changed ? next : current
    })
  }, [location.pathname, sections])

  return (
    <aside className="sidebar">
      <NavLink to={homeTo} className="sidebar__brand">
        <span className="sidebar__brand-mark">
          <BoltIcon size={24} />
        </span>
        <span className="sidebar__brand-text">
          <span>Outfalak</span>Checkout
        </span>
      </NavLink>

      <nav className="sidebar__nav">
        {sections.map((section, index) => (
          <div key={section.title ?? `section-${index}`} style={{ display: 'contents' }}>
            {section.title && <p className="sidebar__section">{section.title}</p>}
            {section.items.map((item) => (
              <NavGroup
                key={item.label}
                item={item}
                open={open[item.label] ?? false}
                onToggle={() => setOpen((current) => ({ ...current, [item.label]: !current[item.label] }))}
              />
            ))}
          </div>
        ))}
      </nav>

      {children}
    </aside>
  )
}

/*
 * A real count, or none at all.
 *
 * These badges sit on every screen, so a fabricated number here was the most
 * persistently visible lie in the app. Each reads the same endpoint its page
 * does; with no store selected there is nothing to count, and the badge simply
 * does not render. Same query keys across the rail, so the parent and child
 * share one request.
 */
function useNavBadge(kind: NavBadge | undefined): number | null {
  const { shop } = useAuth()

  const abandoned = useQuery({
    queryKey: ['abandoned-badge', shop?.id],
    queryFn: () => api<{ summary: { openCount: number } }>('/v1/abandoned', { query: { page: 1, perPage: 1 } }),
    enabled: kind === 'abandoned' && Boolean(shop?.id),
    staleTime: 120_000,
  })

  const orders = useQuery({
    queryKey: ['orders-badge', shop?.id],
    queryFn: () => api<{ total: number }>('/v1/orders', { query: { page: 1, perPage: 1 } }),
    enabled: kind === 'orders' && Boolean(shop?.id),
    staleTime: 120_000,
  })

  const count = kind === 'abandoned' ? abandoned.data?.summary.openCount : kind === 'orders' ? orders.data?.total : undefined
  return count && count > 0 ? count : null
}

function NavCount({ kind, count }: { kind: NavBadge | undefined; count: number | null }): ReactNode {
  if (count === null) return null
  // Abandoned carts want attention; the order total is just information.
  const className = kind === 'abandoned' ? 'nav-item__badge' : 'nav-item__badge nav-item__badge--quiet'
  return <span className={className}>{count > 999 ? '999+' : count}</span>
}

function NavTag({ tag }: { tag: string | undefined }): ReactNode {
  return tag ? <span className="nav-item__badge nav-item__badge--tag">{tag}</span> : null
}

function NavChild({ child }: { child: NavLeaf }): ReactNode {
  const count = useNavBadge(child.badge)
  return (
    <NavLink to={child.to} end={child.end} className={({ isActive }) => (isActive ? 'nav-item is-active' : 'nav-item')}>
      <span className="nav-item__label">{child.label}</span>
      <NavTag tag={child.tag} />
      <NavCount kind={child.badge} count={count} />
    </NavLink>
  )
}

function NavGroup({ item, open, onToggle }: { item: NavNode; open: boolean; onToggle: () => void }): ReactNode {
  const location = useLocation()
  const count = useNavBadge(item.badge)
  const badge = (
    <>
      <NavTag tag={item.tag} />
      <NavCount kind={item.badge} count={count} />
    </>
  )

  if (!item.children) {
    return (
      <NavLink to={item.to} end={item.end} className={({ isActive }) => (isActive ? 'nav-item is-active' : 'nav-item')}>
        {item.icon}
        <span className="nav-item__label">{item.label}</span>
        {badge}
      </NavLink>
    )
  }

  const withinGroup = item.children.some((child) => location.pathname === child.to || location.pathname.startsWith(`${child.to}/`))

  return (
    <>
      <button
        type="button"
        className={['nav-item', open ? 'is-open' : '', !open && withinGroup ? 'is-active' : ''].filter(Boolean).join(' ')}
        aria-expanded={open}
        onClick={onToggle}
      >
        {item.icon}
        <span className="nav-item__label">{item.label}</span>
        {badge}
        <ChevronDownIcon size={15} className="nav-item__chevron" />
      </button>

      {open && (
        <div className="sidebar__children">
          {item.children.map((child) => (
            <NavChild key={child.to} child={child} />
          ))}
        </div>
      )}
    </>
  )
}

// ── Topbar ──────────────────────────────────────────────────────────────────

export function TopbarSearch({ sections, placeholder }: { sections: NavSection[]; placeholder: string }): ReactNode {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')

  const leaves = useMemo(() => navLeaves(sections), [sections])

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (needle.length < 2) return []
    return leaves.filter((leaf) => leaf.label.toLowerCase().includes(needle)).slice(0, 6)
  }, [leaves, query])

  const focusSearch = useCallback((event: KeyboardEvent) => {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault()
      document.getElementById('global-search')?.focus()
    }
  }, [])

  useEffect(() => {
    document.addEventListener('keydown', focusSearch)
    return () => document.removeEventListener('keydown', focusSearch)
  }, [focusSearch])

  return (
      <div className="topbar__search" style={{ position: 'relative' }}>
        <SearchIcon size={16} />
        <input
          id="global-search"
          type="search"
          placeholder={placeholder}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && matches[0]) {
              navigate(matches[0].to)
              setQuery('')
            }
            if (event.key === 'Escape') setQuery('')
          }}
        />
        <kbd className="kbd">⌘K</kbd>

        {matches.length > 0 && (
          <div className="menu__panel menu__panel--left menu__panel--wide" style={{ top: 'calc(100% + 8px)' }} role="listbox">
            {matches.map((match) => (
              <button
                key={match.to}
                type="button"
                className="menu__item"
                onMouseDown={(event) => {
                  event.preventDefault()
                  navigate(match.to)
                  setQuery('')
                }}
              >
                <SearchIcon size={15} />
                <span className="spacer" style={{ textAlign: 'left' }}>
                  {match.label}
                  <small>{match.group}</small>
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
  )
}

export function AccountMenu({
  name,
  email,
  role,
  profileTo,
  settingsTo,
}: {
  name: string
  email: string
  role: string
  profileTo: string
  settingsTo: string
}): ReactNode {
  const { signOut } = useAuth()
  const navigate = useNavigate()

  return (
    <Menu
      label="Account"
      trigger={
        <>
          <span className="avatar">{initials(name)}</span>
          <span className="who">
            <strong>{name}</strong>
            <span>{role}</span>
          </span>
        </>
      }
    >
      {(close) => (
        <>
          <div className="menu__head">
            <strong style={{ fontSize: '.85rem' }}>{name}</strong>
            <small className="faint" style={{ fontSize: '.75rem' }}>
              {email}
            </small>
          </div>

          <MenuItem
            icon={<UserIcon size={16} />}
            onClick={() => {
              navigate(profileTo)
              close()
            }}
          >
            Profile
          </MenuItem>
          <MenuItem
            icon={<GearIcon size={16} />}
            onClick={() => {
              navigate(settingsTo)
              close()
            }}
          >
            Settings
          </MenuItem>

          <div className="menu__sep" />
          <MenuItem danger icon={<LogOutIcon size={16} />} onClick={() => void signOut()}>
            Sign out
          </MenuItem>
        </>
      )}
    </Menu>
  )
}
